import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import TransactionModal from '../components/TransactionModal';
import ExportModal from '../components/ExportModal';

function Transactions({ currentMonth }) {
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [currentMonth]);

  async function loadData() {
    try {
      setLoading(true);
      const [transactionsData, categoriesData] = await Promise.all([
        api.getTransactions(currentMonth),
        api.getCategories(),
      ]);

      setTransactions(transactionsData);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  }

  function formatDate(dateString) {
    // Fix timezone issue by parsing the date string directly
    // dateString format: "2026-01-13"
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString();
  }

  async function handleSaveTransaction(transactionData) {
    try {
      if (editingTransaction) {
        await api.updateTransaction(editingTransaction.id, transactionData);
      } else {
        await api.createTransaction(transactionData);
      }
      setShowModal(false);
      setEditingTransaction(null);
      loadData();
    } catch (error) {
      alert('Failed to save transaction: ' + error.message);
    }
  }

  async function handleDeleteTransaction(id) {
    if (!window.confirm('Are you sure you want to delete this transaction?')) {
      return;
    }

    try {
      await api.deleteTransaction(id);
      loadData();
    } catch (error) {
      alert('Failed to delete transaction: ' + error.message);
    }
  }

  async function handleToggleVerified(transaction) {
    try {
      await api.updateTransaction(transaction.id, {
        ...transaction,
        verified: !transaction.verified,
      });
      setTransactions(prev =>
        prev.map(t => t.id === transaction.id ? { ...t, verified: !t.verified } : t)
      );
    } catch (error) {
      alert('Failed to update transaction: ' + error.message);
    }
  }

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  const totalIncome = transactions
    .filter(t => t.type === 'income')
    .reduce((sum, t) => sum + t.amount, 0);

  const totalExpenses = transactions
    .filter(t => t.type === 'expense')
    .reduce((sum, t) => sum + t.amount, 0);

  return (
    <div className="transactions-page">
      <div className="page-actions">
        <button
          onClick={() => {
            setEditingTransaction(null);
            setShowModal(true);
          }}
          className="btn btn-primary"
        >
          + Add Transaction
        </button>
        <button onClick={() => setShowExportModal(true)} className="btn btn-secondary">
          Export to CSV
        </button>
      </div>

      <div className="summary-cards">
        <div className="summary-card income-card">
          <div className="summary-label">Total Income</div>
          <div className="summary-value">${totalIncome.toFixed(2)}</div>
        </div>
        <div className="summary-card expense-card">
          <div className="summary-label">Total Expenses</div>
          <div className="summary-value">${totalExpenses.toFixed(2)}</div>
        </div>
        <div className="summary-card balance-card">
          <div className="summary-label">Net</div>
          <div className="summary-value">${(totalIncome - totalExpenses).toFixed(2)}</div>
        </div>
      </div>

      <div className="transactions-list">
        {transactions.length === 0 && (
          <p className="empty-state">No transactions for this month. Add one to get started!</p>
        )}
        
        {transactions.map(transaction => (
          <div key={transaction.id} className="transaction-item">
            <div className="transaction-main">
              <div className="transaction-date">
                {formatDate(transaction.date)}
              </div>
              <div className="transaction-info">
                <div className="transaction-merchant">{transaction.merchant}</div>
                <div className="transaction-category">
                  {transaction.category_name}
                  {transaction.split_category_id && (
                    <span className="split-indicator">
                      {' '}→ {transaction.split_category_name} (${transaction.split_amount?.toFixed(2)})
                    </span>
                  )}
                </div>
                {transaction.description && (
                  <div className="transaction-description">{transaction.description}</div>
                )}
              </div>
              <div className={`transaction-amount ${transaction.type} ${transaction.amount < 0 ? 'negative' : ''}`}>
                {transaction.amount < 0 
                  ? (transaction.type === 'income' ? '-' : '↩ ') 
                  : (transaction.type === 'income' ? '+' : '-')}
                ${Math.abs(transaction.amount).toFixed(2)}
              </div>
            </div>
            <div className="transaction-actions">
              <button
                onClick={() => handleToggleVerified(transaction)}
                className={`btn-verify ${transaction.verified ? 'verified' : ''}`}
                title={transaction.verified ? 'Verified' : 'Not verified'}
              >
                {transaction.verified ? '✓' : '○'}
              </button>
              {transaction.is_recurring && (
                <span className="recurring-badge" title="Recurring">⟳</span>
              )}
              <button
                onClick={() => {
                  setEditingTransaction(transaction);
                  setShowModal(true);
                }}
                className="btn-icon"
                title="Edit"
              >
                ✎
              </button>
              <button
                onClick={() => handleDeleteTransaction(transaction.id)}
                className="btn-icon"
                title="Delete"
              >
                ✕
              </button>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <TransactionModal
          transaction={editingTransaction}
          categories={categories}
          onSave={handleSaveTransaction}
          onClose={() => {
            setShowModal(false);
            setEditingTransaction(null);
          }}
        />
      )}

      {showExportModal && (
        <ExportModal
          categories={categories}
          transactions={transactions}
          onClose={() => setShowExportModal(false)}
        />
      )}
    </div>
  );
}

export default Transactions;
